import {
  WhiteAgiosLogo,
  WhiteBluebirdLogo,
  WhiteChiesiLogo,
  WhiteNovoNordiskLogo,
  WhitePfizerLogo,
  QuiltHealthVertWhiteLogo,
  SanofiLogo,
  VertexLogo,
  BeamTransparentLogo,
  BristolMeyersLogo,
  FulcrumLogo,
  GamidaCellTransparentLogo,
  MedunikTransparentLogo,
  TerumoLogo
} from 'assets'
import React from 'react'
import { Link } from 'react-router-dom'

interface SponsorLogo {
  name: string
  image: string
  link: string
}

interface SponsorLogoBannerProps {
  className?: string
}

export const SponsorLogoBanner: React.FC<SponsorLogoBannerProps> = ({
  className
}) => {
  // Diamond sponsor
  const diamondSponsor: SponsorLogo = {
    name: 'Sanofi',
    image: SanofiLogo,
    link: 'https://www.sanofi.com/'
  }

  // Platinum sponsors
  const platinumSponsors: SponsorLogo[] = [
    {
      name: 'Novo Nordisk',
      image: WhiteNovoNordiskLogo,
      link: 'https://www.novonordisk.com/'
    },
    {
      name: 'Agios',
      image: WhiteAgiosLogo,
      link: 'https://www.agios.com/'
    },
    {
      name: 'Chiesi',
      image: WhiteChiesiLogo,
      link: 'https://www.chiesi.com/'
    },
    {
      name: 'Bluebird Bio',
      image: WhiteBluebirdLogo,
      link: 'https://www.bluebirdbio.com/'
    },
    {
      name: 'Vertex',
      image: VertexLogo,
      link: 'https://www.vrtx.com/'
    },
    {
      name: 'Quilt Health',
      image: QuiltHealthVertWhiteLogo,
      link: 'https://www.quilthealth.com/'
    }
  ]

  // Gold sponsors
  const goldSponsors: SponsorLogo[] = [
    {
      name: 'Terumo',
      image: TerumoLogo,
      link: 'https://www.terumo.com/'
    },
    {
      name: 'Fulcrum Therapeutics',
      image: FulcrumLogo,
      link: 'https://www.fulcrumtx.com/'
    },
    {
      name: 'Medunik',
      image: MedunikTransparentLogo,
      link: 'https://www.medunik.com/'
    },
    {
      name: 'Gamida Cell',
      image: GamidaCellTransparentLogo,
      link: 'https://www.gamida-cell.com/'
    },
    {
      name: 'Bristol Meyers Squibb',
      image: BristolMeyersLogo,
      link: 'https://www.bms.com/'
    },
    {
      name: 'Pfizer',
      image: WhitePfizerLogo,
      link: 'https://www.pfizer.com/'
    },
    {
      name: 'BEAM Therapeutics',
      image: BeamTransparentLogo,
      link: 'https://www.beamtx.com/'
    }
  ]

  const getLogoClassName = (logo: SponsorLogo) => {
    let baseClass = 'h-10 w-auto max-w-[120px] object-contain'

    // Special scaling for specific logos
    if (logo.name === 'Sanofi') baseClass += ' scale-[2.5]'
    else if (logo.name === 'Quilt Health') baseClass += ' scale-[3.5]'
    else if (logo.name === 'Medunik') baseClass += ' scale-[2.5]'
    else if (logo.name === 'Bluebird Bio') baseClass += ' scale-[1.25]'

    // Add brightness filter for colored logos on dark background
    if (
      logo.name === 'Sanofi' ||
      logo.name === 'Terumo' ||
      logo.name === 'Fulcrum Therapeutics' ||
      logo.name === 'Bristol Meyers Squibb' ||
      logo.name === 'Gamida Cell' ||
      logo.name === 'Medunik' ||
      logo.name === 'BEAM Therapeutics' ||
      logo.name === 'Vertex'
    ) {
      baseClass += ' brightness-0 invert'
    }

    return baseClass
  }

  return (
    <section className={`py-6 px-8 ${className}`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="text-white md:self-center">
            <h3 className="text-lg font-medium">Our Sponsors:</h3>
            <Link
              to="/sponsors"
              className="text-xs underline hover:no-underline"
            >
              Learn more about becoming
              <br />a corporate sponsor.
            </Link>
          </div>
          <div className="flex-1 flex flex-col gap-6">
            {/* Diamond Sponsor */}
            <div className="flex justify-center">
              <Link to={diamondSponsor.link} className="flex items-center">
                <img
                  src={diamondSponsor.image}
                  alt={diamondSponsor.name}
                  className={getLogoClassName(diamondSponsor)}
                />
              </Link>
            </div>

            {/* Platinum Sponsors */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6 justify-items-center items-center">
              {platinumSponsors.map((logo) => (
                <Link
                  key={logo.name}
                  to={logo.link}
                  className="flex items-center"
                >
                  <img
                    src={logo.image}
                    alt={logo.name}
                    className={getLogoClassName(logo)}
                  />
                </Link>
              ))}
            </div>

            {/* Gold Sponsors */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-4 sm:gap-6 justify-items-center items-center">
              {goldSponsors.map((logo) => (
                <Link
                  key={logo.name}
                  to={logo.link}
                  className="flex items-center"
                >
                  <img
                    src={logo.image}
                    alt={logo.name}
                    className={getLogoClassName(logo)}
                  />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
